/**
 * Customer Authentication Context
 * Handles customer signup, login, and authentication.
 * Supports both per-business customer login and "Sign in with Mango" (SSO).
 * When mango_token is present we use the main auth API (/auth/me) and treat the user as logged in.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_ENDPOINTS, BUSINESS_ID, API_BASE_URL, MANGO_SSO_URL } from '../config/api';

const MANGO_TOKEN_KEY = 'mango_token';

const CustomerAuthContext = createContext();

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Resolve user from Mango token (main auth /auth/me)
  const resolveMangoUser = async (mangoToken) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${mangoToken}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const u = data.user || data.account;
      if (!u) return null;
      return {
        id: u.id,
        mangoId: u.mangoId,
        email: u.email,
        firstName: u.firstName || u.email?.split('@')[0] || '',
        lastName: u.lastName || '',
        tier: u.tier,
      };
    } catch {
      return null;
    }
  };

  // Load customer/token on mount and handle SSO return (#mango_token=...)
  useEffect(() => {
    const hash = window.location.hash || '';
    const match = hash.match(/#mango_token=([^&]+)/);
    if (match) {
      const mangoToken = decodeURIComponent(match[1]);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      localStorage.setItem(MANGO_TOKEN_KEY, mangoToken);
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_data');
      resolveMangoUser(mangoToken).then((user) => {
        setToken(mangoToken);
        setCustomer(user);
        if (user) localStorage.setItem('customer_data', JSON.stringify(user));
        setIsLoading(false);
      });
      return;
    }

    const savedToken = localStorage.getItem('customer_token');
    const mangoToken = localStorage.getItem(MANGO_TOKEN_KEY);
    const savedCustomer = localStorage.getItem('customer_data');

    if (mangoToken) {
      setToken(mangoToken);
      if (savedCustomer) {
        try {
          setCustomer(JSON.parse(savedCustomer));
        } catch {
          resolveMangoUser(mangoToken).then((user) => {
            setCustomer(user);
            if (user) localStorage.setItem('customer_data', JSON.stringify(user));
          });
        }
      } else {
        resolveMangoUser(mangoToken).then((user) => {
          setCustomer(user);
          if (user) localStorage.setItem('customer_data', JSON.stringify(user));
        });
      }
      setIsLoading(false);
      return;
    }

    if (savedToken && savedCustomer) {
      setToken(savedToken);
      setCustomer(JSON.parse(savedCustomer));
    }
    setIsLoading(false);
  }, []);

  // Signup
  const signup = async (userData) => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.customerAuth.signup, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userData,
          businessId: BUSINESS_ID,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Store token and customer data
      setToken(data.token);
      setCustomer(data.customer);
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));

      return data.customer;
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Login
  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.customerAuth.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          businessId: BUSINESS_ID,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token and customer data
      setToken(data.token);
      setCustomer(data.customer);
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));

      return data.customer;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = () => {
    setCustomer(null);
    setToken(null);
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    localStorage.removeItem(MANGO_TOKEN_KEY);
  };

  const getAuthHeaders = () => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  const getSignInWithMangoUrl = () => {
    const returnUrl = window.location.origin + (window.location.pathname || '') + (window.location.search || '');
    return `${MANGO_SSO_URL}/sso?return_url=${encodeURIComponent(returnUrl)}`;
  };

  // Refresh customer data
  const refreshCustomer = async () => {
    if (!token) return null;

    try {
      const response = await fetch(API_ENDPOINTS.customerProfile.me, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer data');
      }

      const data = await response.json();
      setCustomer(data.customer);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));

      return data.customer;
    } catch (error) {
      console.error('Failed to refresh customer:', error);
      // If token is invalid, logout
      if (error.message.includes('401') || error.message.includes('403')) {
        logout();
      }
      return null;
    }
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        token,
        isLoading,
        isAuthenticated: !!customer && !!token,
        signup,
        login,
        logout,
        getAuthHeaders,
        refreshCustomer,
        getSignInWithMangoUrl,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

